import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../components/Skeleton.js";
import { api, ApiError, ReplayJob, loadSession } from "../lib/api.js";
import { useTableState } from "../lib/useTableState.js";
import TableToolbar from "../components/TableToolbar.js";

type TabKey = "all" | "running" | "completed" | "failed" | "replay" | "metadata";

const TABS: { key: TabKey; label: string; matcher: (j: ReplayJob) => boolean }[] = [
  { key: "all",       label: "All",           matcher: () => true },
  { key: "running",   label: "Running",       matcher: (j) => j.status === "running" || j.status === "queued" },
  { key: "completed", label: "Completed",     matcher: (j) => j.status === "succeeded" || j.status === "completed" },
  { key: "failed",    label: "Failed",        matcher: (j) => j.status === "failed" || j.status === "dead-lettered" },
  { key: "replay",    label: "Replay",        matcher: (j) => j.type === "replay" },
  { key: "metadata",  label: "Metadata sync", matcher: (j) => typeof j.type === "string" && j.type.includes("metadata") }
];

function jobPill(status?: string) {
  switch (status) {
    case "succeeded":
    case "completed":      return <span className="pill pill-ok">{status}</span>;
    case "running":        return <span className="pill pill-info">Running</span>;
    case "queued":         return <span className="pill pill-muted">Queued</span>;
    case "failed":         return <span className="pill pill-err">Failed</span>;
    case "dead-lettered":  return <span className="pill pill-err">Dead-lettered</span>;
    default:               return <span className="pill pill-muted">{status ?? "—"}</span>;
  }
}

function fmt(date?: string): string {
  return date ? new Date(date).toLocaleString() : "—";
}

export default function Jobs() {
  const session = loadSession();
  const isAdmin = session?.role === "admin";

  const [jobs, setJobs] = useState<ReplayJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [selected, setSelected] = useState<ReplayJob | null>(null);
  const [triggerOpen, setTriggerOpen] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerErr, setTriggerErr] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobsResp, replayResp] = await Promise.all([
        api.jobs().catch(() => ({ jobs: [] as ReplayJob[] })),
        api.replayJobs().catch(() => ({ jobs: [] as ReplayJob[] }))
      ]);
      const merged = new Map<string, ReplayJob>();
      for (const j of [...(jobsResp.jobs ?? []), ...(replayResp.jobs ?? [])]) {
        merged.set(j.id, j);
      }
      const list = [...merged.values()].sort((a, b) => {
        const at = a.createdAt ?? (a as { requestedAt?: string }).requestedAt ?? "";
        const bt = b.createdAt ?? (b as { requestedAt?: string }).requestedAt ?? "";
        return bt.localeCompare(at);
      });
      setJobs(list);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10_000);
    return () => clearInterval(id);
  }, []);

  const visibleJobs = useMemo(() => {
    const match = TABS.find((t) => t.key === activeTab)?.matcher ?? (() => true);
    return jobs.filter(match);
  }, [jobs, activeTab]);

  const counts = useMemo(() => {
    const c: Partial<Record<TabKey, number>> = {};
    for (const t of TABS) c[t.key] = jobs.filter(t.matcher).length;
    return c;
  }, [jobs]);

  const t = useTableState(visibleJobs as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "type", "status"],
    initialPageSize: 25
  });

  const onTriggerReplay = async (scope: "full" | "incremental") => {
    if (!isAdmin) return;
    setTriggering(true);
    setTriggerErr(null);
    try {
      await api.runReplay({ scope });
      setTriggerOpen(false);
      await refresh();
    } catch (e) {
      setTriggerErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTriggering(false);
    }
  };

  const onSyncMetadata = async () => {
    if (!isAdmin) return;
    setTriggering(true);
    setTriggerErr(null);
    try {
      await api.request("/api/aggregate-metadata/sync", { method: "POST" });
      setTriggerOpen(false);
      await refresh();
    } catch (e) {
      setTriggerErr(e instanceof Error ? e.message : String(e));
    } finally {
      setTriggering(false);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Ingestion jobs</h1>
          <p>Replays, metadata syncs, and background ingestion — monitor, retry, and trigger new runs.</p>
        </div>
        <div className="actions">
          <button className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
          <button
            className="btn btn-primary"
            onClick={() => setTriggerOpen(true)}
            disabled={!isAdmin}
            title={isAdmin ? undefined : "Admins only"}
          >
            Run ingestion
          </button>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="tabs" style={{ display: "flex", gap: 4, flexWrap: "wrap", borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`btn ${activeTab === t.key ? "btn-primary" : ""}`}
            style={{ borderRadius: "6px 6px 0 0" }}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label} <span className="pill pill-muted" style={{ marginLeft: 6 }}>{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {loading && jobs.length === 0 ? (
        <SkeletonTable rows={5} columns={4} />
      ) : visibleJobs.length === 0 ? (
        <div className="empty">
          <p>No jobs in this tab.</p>
          {isAdmin && activeTab === "all" && (
            <p className="muted">
              Use <strong>Run ingestion</strong> above to trigger your first replay or metadata sync.
            </p>
          )}
        </div>
      ) : (
        <div className="table-scroll">
          <TableToolbar
            search={t.search} setSearch={t.setSearch}
            page={t.page} setPage={t.setPage}
            pageSize={t.pageSize} setPageSize={t.setPageSize}
            totalPages={t.totalPages} totalRows={t.totalRows}
            hideExport
            searchPlaceholder="Search jobs…"
          />
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Type</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Created</th>
                <th>Updated</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(t.paged as unknown as ReplayJob[]).map((j) => (
                <tr
                  key={j.id}
                  onClick={() => setSelected(j)}
                  style={{ cursor: "pointer" }}
                >
                  <td><code>{j.id.slice(0, 12)}…</code></td>
                  <td>{j.type ?? "—"}</td>
                  <td>{jobPill(j.status)}</td>
                  <td>{typeof j.progress === "number" ? `${Math.round(j.progress)}%` : "—"}</td>
                  <td className="soft">{fmt(j.createdAt ?? (j as { requestedAt?: string }).requestedAt)}</td>
                  <td className="soft">{fmt(j.updatedAt ?? (j as { completedAt?: string }).completedAt)}</td>
                  <td>
                    <button
                      type="button"
                      className="linkbtn"
                      onClick={(e) => { e.stopPropagation(); setSelected(j); }}
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <JobDetailDrawer job={selected} onClose={() => setSelected(null)} />
      )}

      {triggerOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50
          }}
          onClick={() => !triggering && setTriggerOpen(false)}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 480, width: "92%", background: "#fff" }}
          >
            <div className="card-head"><strong>Run ingestion</strong></div>
            <div className="card-body">
              <p>Pick a job to enqueue. Jobs run in the background — you'll see progress on this page.</p>
              {triggerErr && <div className="alert alert-error">{triggerErr}</div>}
              <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                <button className="btn" disabled={triggering} onClick={() => void onSyncMetadata()}>
                  <strong>Sync aggregate metadata</strong>
                  <div className="muted" style={{ fontSize: 12 }}>Pull programs, indicators, org units from the active DHIS2 instance.</div>
                </button>
                <button className="btn" disabled={triggering} onClick={() => void onTriggerReplay("incremental")}>
                  <strong>Run incremental replay</strong>
                  <div className="muted" style={{ fontSize: 12 }}>Process new submitted aggregates since last run.</div>
                </button>
                <button className="btn" disabled={triggering} onClick={() => void onTriggerReplay("full")}>
                  <strong>Run full replay</strong>
                  <div className="muted" style={{ fontSize: 12 }}>Re-process the full window. Slow — only when needed.</div>
                </button>
              </div>
            </div>
            <div className="card-foot" style={{ textAlign: "right", padding: 12 }}>
              <button className="btn" disabled={triggering} onClick={() => setTriggerOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function JobDetailDrawer({ job, onClose }: { job: ReplayJob; onClose: () => void }) {
  const [detail, setDetail] = useState<ReplayJob | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.request<{ job: ReplayJob }>(`/api/jobs/${encodeURIComponent(job.id)}`)
      .then((d) => { if (!cancelled) setDetail(d.job); })
      .catch((e: unknown) => { if (!cancelled) setDetailErr(e instanceof Error ? e.message : String(e)); });
    return () => { cancelled = true; };
  }, [job.id]);

  const view: ReplayJob = detail ?? job;
  const input = (view as { input?: unknown }).input;
  const result = (view as { result?: unknown }).result;
  const err = (view as { error?: unknown }).error;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.3)",
        display: "flex", justifyContent: "flex-end", zIndex: 40
      }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(520px, 92%)", height: "100%", overflowY: "auto", background: "#fff" }}
      >
        <div className="card-head" style={{ display: "flex", justifyContent: "space-between" }}>
          <strong>Job · {view.id}</strong>
          <button className="linkbtn" onClick={onClose}>Close</button>
        </div>
        <div className="card-body">
          {detailErr && <div className="alert alert-warning">Couldn't load detail: {detailErr}</div>}
          <dl className="dl-kv">
            <dt>Type</dt><dd>{view.type ?? "—"}</dd>
            <dt>Status</dt><dd>{jobPill(view.status)}</dd>
            <dt>Progress</dt><dd>{typeof view.progress === "number" ? `${Math.round(view.progress)}%` : "—"}</dd>
            <dt>Created</dt><dd>{fmt(view.createdAt ?? (view as { requestedAt?: string }).requestedAt)}</dd>
            <dt>Updated</dt><dd>{fmt(view.updatedAt ?? (view as { completedAt?: string }).completedAt)}</dd>
          </dl>
          {err != null && (
            <>
              <h3 style={{ marginTop: 16 }}>Error</h3>
              <pre style={{ whiteSpace: "pre-wrap", background: "#fef2f2", padding: 8, borderRadius: 4 }}>
                {typeof err === "string" ? err : JSON.stringify(err, null, 2)}
              </pre>
            </>
          )}
          {input != null && (
            <>
              <h3 style={{ marginTop: 16 }}>Input</h3>
              <pre style={{ whiteSpace: "pre-wrap", background: "#f8fafc", padding: 8, borderRadius: 4 }}>
                {JSON.stringify(input, null, 2)}
              </pre>
            </>
          )}
          {result != null && (
            <>
              <h3 style={{ marginTop: 16 }}>Result</h3>
              <pre style={{ whiteSpace: "pre-wrap", background: "#f8fafc", padding: 8, borderRadius: 4 }}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
